import type { FC } from "react";

import { useQuotaForecastingStats } from "../../hooks/useQuotaForecastingStats";
import { formatTokenCount, formatUsd } from "../../utils/currency";
import { DataPanel } from "../common/DataPanel";
import { DataState } from "../common/DataState";
import { MetricCard } from "../common/MetricCard";
import { SectionHeader } from "../common/SectionHeader";
import { QuotaForecastingEntriesTable } from "./QuotaForecastingEntriesTable";
import { QuotaForecastingFiltersPanel } from "./QuotaForecastingFiltersPanel";
import { QuotaForecastingHistogramChart } from "./QuotaForecastingHistogramChart";
import { QuotaForecastingLoadChart } from "./QuotaForecastingLoadChart";

function formatPercent(value: number | null): string {
  if (value === null) {
    return "\u2014";
  }
  return `${value.toFixed(0)}%`;
}

export const QuotaForecastingView: FC = () => {
  const quota = useQuotaForecastingStats({
    debounceMs: 250,
    initialPageSize: 50,
  });

  return (
    <div className="space-y-4">
      <DataPanel
        title="Quota Forecasting"
        subtitle="Inspect rolling demand, per-call token sizes, and context pressure for provider quotas."
        icon={
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 13l4-4 4 4 8-8 2 2-10 10-4-4-4 4V13z"
            />
          </svg>
        }
        iconColor="sky"
        className="hover:shadow-none"
      >
        <QuotaForecastingFiltersPanel
          filters={quota.filters}
          isLoading={quota.isLoading}
          onSetDateRange={quota.setDateRange}
          onSetFilters={quota.setFilters}
          onRefresh={quota.refresh}
          onReset={quota.reset}
        />
      </DataPanel>

      <DataState
        loading={quota.isLoading && !quota.summary}
        error={quota.error?.message ?? null}
        empty={
          !quota.isLoading && Boolean(quota.summary) && quota.totalRows === 0
        }
        emptyTitle="No quota telemetry"
        emptyDescription="Quota forecasting starts collecting data after this deploy. Broaden the time range once new calls arrive."
        className="space-y-4"
      >
        <div className="space-y-3">
          <SectionHeader title="Summary" />
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <MetricCard
              label="Peak Rolling TPM"
              value={formatTokenCount(
                quota.summary?.peaks.rollingTotalTokensPerMinute ?? null,
              )}
              variant="emerald"
            />
            <MetricCard
              label="Peak Rolling RPM"
              value={
                quota.summary?.peaks.rollingRequestsPerMinute.toLocaleString() ??
                "\u2014"
              }
              variant="indigo"
            />
            <MetricCard
              label="Window Tokens"
              value={formatTokenCount(quota.totals?.totalTokens ?? null)}
              variant="amber"
            />
            <MetricCard
              label="P95 Tokens / Call"
              value={formatTokenCount(
                quota.summary?.percentiles.p95TokensPerCall ?? null,
              )}
              variant="indigo"
            />
            <MetricCard
              label="P95 Context"
              value={formatTokenCount(
                quota.summary?.percentiles.p95ContextWindowSize ?? null,
              )}
              variant="violet"
            />
          </div>
        </div>

        <QuotaForecastingLoadChart
          buckets={quota.summary?.minute_buckets ?? []}
        />

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label="Successful Calls"
            value={
              quota.totals?.successfulRequestCount.toLocaleString() ?? "\u2014"
            }
            variant="emerald"
          />
          <MetricCard
            label="Throttled Calls"
            value={
              quota.totals?.throttledRequestCount.toLocaleString() ?? "\u2014"
            }
            variant="amber"
          />
          <MetricCard
            label="Failed Calls"
            value={
              quota.totals?.failedRequestCount.toLocaleString() ?? "\u2014"
            }
            variant="rose"
          />
          <MetricCard
            label="Calls In Window"
            value={quota.totals?.requestCount.toLocaleString() ?? "\u2014"}
            variant="indigo"
          />
        </div>

        <div className="grid gap-3 lg:grid-cols-2">
          <QuotaForecastingHistogramChart
            title="Tokens Per Call"
            buckets={quota.summary?.histograms.tokens_per_call ?? []}
            emptyText="No call-size histogram is available yet."
            fill="rgba(59, 130, 246, 0.72)"
          />
          <QuotaForecastingHistogramChart
            title="Context Window Size"
            buckets={quota.summary?.histograms.context_window_size ?? []}
            emptyText="No context-window histogram is available yet."
            fill="rgba(20, 184, 166, 0.72)"
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label="Input Tokens"
            value={formatTokenCount(quota.totals?.inputTokens ?? null)}
            variant="indigo"
          />
          <MetricCard
            label="Output Tokens"
            value={formatTokenCount(quota.totals?.outputTokens ?? null)}
            variant="indigo"
          />
          <MetricCard
            label="Peak Budget Use"
            value={formatPercent(
              quota.entriesRows.reduce<number | null>((max, entry) => {
                if (entry.budget_percent_used === null) {
                  return max;
                }
                return max === null
                  ? entry.budget_percent_used
                  : Math.max(max, entry.budget_percent_used);
              }, null),
            )}
            variant="amber"
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label="Peak Tokens / Call"
            value={formatTokenCount(
              quota.summary?.peaks.maxTokensPerCall ?? null,
            )}
            variant="rose"
          />
          <MetricCard
            label="Peak Context"
            value={formatTokenCount(
              quota.summary?.peaks.maxContextWindowSize ?? null,
            )}
            variant="violet"
          />
          <MetricCard
            label="Cached Input"
            value={formatTokenCount(quota.totals?.cachedInputTokens ?? null)}
            variant="emerald"
          />
          <MetricCard
            label="Approx Cost"
            value={formatUsd(
              quota.entriesRows.reduce(
                (sum, entry) => sum + (entry.estimated_cost_usd ?? 0),
                0,
              ),
            )}
            variant="indigo"
          />
        </div>

        <QuotaForecastingEntriesTable
          entries={quota.entriesRows}
          total={quota.totalRows}
          page={quota.page}
          pageSize={quota.pageSize}
          isLoading={quota.isLoading}
          errorMessage={quota.error?.message ?? null}
          onPageChange={quota.setPage}
        />
      </DataState>
    </div>
  );
};
